import { Box, Link, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';

import { PageHeaderAction, PageHeaderBreadcrumb } from './types';
import { Breadcrumbs } from '../../mui-wrapper-components/Breadcrumbs/Breadcrumbs';
import { Button as ButtonV2 } from '../../mui-wrapper-components/Button/Button';
import { Icon as IconV2 } from '../../mui-wrapper-components/Icon/Icon';
import { Tooltip } from '../../mui-wrapper-components/Tooltip/Tooltip';
import { PageTabProps } from '../PageTabs/PageTab';
import PageTabs from '../PageTabs/PageTabs';
import { StatusDisplay } from '../StatusDisplay/StatusDisplay';

export type PageHeaderProps = {
  /**
   * @prop {string} - The component ID, is used as part of test id's
   */
  id: string;

  /**
   * @prop {boolean} - breadcrumb leading icon
   * @default false
   */
  breadcrumbLeadingIcon?: boolean;

  /**
   * @prop {PageHeaderBreadcrumb[]} - The breadcrumb link definitions
   */
  breadcrumbs?: PageHeaderBreadcrumb[];

  /**
   * @prop {PageHeaderAction[]} - The action button definitions
   */
  actions?: PageHeaderAction[];

  /**
   * @prop {string} - The page title
   */
  pageTitle: string;

  /**
   * @prop {string} - The page title
   */
  pageTitleIcon?: ReactNode;

  /**
   * @prop {ReactNode} - Placeholder for adding a status component or information bar
   */
  infoBarContainer?: ReactNode;

  /**
   * @prop {string} - The status type for header display
   */
  status?: string;

  /**
   * @prop {PageTabProps[]} - The page tabs definitions
   */
  pageTabs?: PageTabProps[];

  /**
   * @prop {PageTabOnChange} - The function to call when the tab is clicked
   */
  onPageTabChange?: (e: React.SyntheticEvent, tab: string) => void;

  /**
   * @prop {React.ReactNode} - The page description
   */
  description?: React.ReactNode;

  /**
   * @prop {string} - Title below the pageTabs
   */
  titleBelowTabs?: string;

  /**
   * @prop {React.ReactNode} - Description below the pageTabs
   */
  descriptionBelowTabs?: React.ReactNode;
};

// TODO: @art.kulakow - Cleanup this component
export const PageHeader = ({
  id,
  breadcrumbLeadingIcon,
  breadcrumbs,
  actions,
  pageTitle,
  pageTitleIcon,
  status,
  pageTabs,
  onPageTabChange,
  description,
  infoBarContainer,
  titleBelowTabs,
  descriptionBelowTabs,
}: PageHeaderProps) => {
  const theme = useTheme();

  const loadBreadcrumbsAndActions = () => {
    if (!breadcrumbs && !actions) return null;

    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
        data-testid="pageHeader-breadcrumbs"
      >
        <Box sx={{ display: 'flex', flex: 2, alignItems: 'start' }}>
          {breadcrumbs && (
            <>
              {breadcrumbLeadingIcon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    height: `${theme.spacing(4)}`,
                    marginRight: `${theme.spacing(2)}`,
                    mt: `${theme.spacing(1)}`,
                  }}
                >
                  <IconV2
                    id={`pageHeader-breadcrumb-leading-icon-${id}`}
                    icon={'arrowLeft'}
                    width={16}
                    height={16}
                  />
                </Box>
              )}
              <Breadcrumbs
                id={`pageHeader-breadcrumbs-${id}`}
                separator={'/'}
                sx={{ color: '#666666', mb: ` ${theme.spacing(2)}` }}
              >
                {breadcrumbs.map(({ label, onClick, href }, index) => {
                  if (breadcrumbs.length !== 1 && index === breadcrumbs.length - 1) {
                    return (
                      <Link
                        key={label}
                        underline={'none'}
                        onClick={onClick}
                        typography={'body1'}
                        data-testid={`pageHeader-breadcrumbs-link-${index + 1}-${id}`}
                        sx={{
                          fontWeight: '400',
                          cursor: 'default',
                          color: `${theme.palette.text.primary}`,
                        }}
                      >
                        {label}
                      </Link>
                    );
                  }
                  return (
                    <Link
                      key={label}
                      underline={'hover'}
                      typography={'body1'}
                      onClick={onClick}
                      data-testid={`pageHeader-breadcrumbs-link-${index + 1}-${id}`}
                      sx={{
                        fontWeight: '400',
                        cursor: 'pointer',
                        color: `${theme.palette.text.secondary}`,
                      }}
                    >
                      {label}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box id={id} data-testid={id}>
      <Box
        data-testid="pageHeader-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: `${theme.spacing(4)}`,
          width: '100%',
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {loadBreadcrumbsAndActions()}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {pageTitleIcon && (
                  <Box
                    sx={{
                      width: `${theme.spacing(11)}`,
                      height: `${theme.spacing(9)}`,
                      marginRight: `${theme.spacing(1)}`,
                    }}
                  >
                    {pageTitleIcon}
                  </Box>
                )}
                <Typography data-testid={`${id}-title`} variant="h4" sx={{ flex: 3 }}>
                  {pageTitle}
                </Typography>
              </Box>
            </Box>
            <Box>
              {actions && (
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    gap: `${theme.spacing(4)}`,
                  }}
                >
                  {actions.map(
                    (
                      {
                        id,
                        label,
                        btnType,
                        disabled,
                        startIcon,
                        onClickHandler,
                        customRenderer,
                        waitButton,
                        tooltipText,
                      },
                      index
                    ) => {
                      if (customRenderer) {
                        return customRenderer;
                      }

                      return (
                        <Tooltip
                          key={label}
                          id={`${id}-tooltip`}
                          title={tooltipText}
                          placement="left"
                          arrow
                        >
                          {/* span is needed to display tooltip when button is disabled */}
                          <span>
                            <ButtonV2
                              id={id}
                              displayLabel={label}
                              data-testid={`pageHeader-action-button-${label}`}
                              btnType={btnType}
                              disabled={disabled}
                              startIcon={startIcon}
                              onClickHandler={onClickHandler}
                              waitButton={waitButton}
                            />
                          </span>
                        </Tooltip>
                      );
                    }
                  )}
                </Box>
              )}
            </Box>
          </Box>
          {status && (
            <Box sx={{ marginTop: `${theme.spacing(2)}` }}>{<StatusDisplay status={status} />}</Box>
          )}
          {infoBarContainer && <Box>{infoBarContainer}</Box>}
        </Box>
        <Box>
          {description && (
            <Box sx={{ marginTop: `${theme.spacing(4)}` }}>
              <Typography
                variant="body1"
                color={`${theme.palette.text.primary}`}
                sx={{ width: '75%' }}
              >
                {description}
              </Typography>
            </Box>
          )}
          {pageTabs && (
            <Box sx={{ mt: `${theme.spacing(0)}` }} data-testid="pageHeader-pageTabs">
              <PageTabs tabs={pageTabs} onChange={onPageTabChange} id={'pageHeader-tabs'} />
            </Box>
          )}
          {titleBelowTabs && (
            <Box sx={{ mt: `${theme.spacing(6)}` }}>
              <Typography variant="h5">{titleBelowTabs}</Typography>
            </Box>
          )}
          {descriptionBelowTabs && (
            <Box sx={{ mt: `${theme.spacing(4)}` }}>
              <Typography
                variant="body1"
                color={`${theme.palette.text.primary}`}
                sx={{ width: '75%' }}
              >
                {descriptionBelowTabs}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
